<script setup>
import { computed } from 'vue';

import { HrCircularProgress, HrIcon } from '../primitives';

/**
 * Upload progress indicator — a circular ring that transitions to a check mark when upload completes.
 *
 * @status recommended
 * @group status-feedback
 * @useWhen Showing file upload progress inline, e.g. in a toolbar or list row
 * @related HrCompletionIndicator
 */
defineOptions({ name: 'HrUploadProgressIndicator' });

const props = defineProps({
  /**
   * Upload progress (0–100). When 100 the icon switches to a check.
   * @example 60
   */
  value: {
    type: Number,
    required: true,
  },
  /**
   * Legacy props forwarded to the underlying Quasar component — e.g. `{ color: 'primary' }` for non-white toolbar
   * surfaces.
   * @deprecated Only for backwards compatibility with the legacy theme — ignored when a design system theme is active.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const icon = computed(() => (props.value >= 100 ? 'check' : 'cloud_upload'));

// @todo Remove when legacy theme is removed.
const mergedLegacyProps = computed(() => ({
  color: 'white',
  'track-color': 'grey-5',
  thickness: 0.22,
  ...props.legacyProps,
}));
</script>

<template>
  <hr-circular-progress
    v-bind="$props"
    size="30px"
    color-role="on-surface"
    track-variant="subtle"
    show-value
    :legacy-props="mergedLegacyProps"
  >
    <hr-icon
      :name="icon"
      size="xs"
      :legacy-props="{ size: 'xs', color: mergedLegacyProps.color }"
    />
  </hr-circular-progress>
</template>
